import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Tabs, Tab, CircularProgress, Typography, Button, Tooltip } from "@material-ui/core";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import NewReleasesIcon from "@material-ui/icons/NewReleases";
import DateRangeIcon from "@material-ui/icons/DateRange";
import Header from "@components/Header";
import { useAuth } from "@hooks/useAuth";
import { useSeenVersion } from "@hooks/useSeenVersion";
import MarkdownRenderer from "./components/MarkdownRenderer";
import TableOfContents from "./components/TableOfContents";
import ChangelogSidebar from "./components/ChangelogSidebar";
import ChangelogVersionView from "./components/ChangelogVersionView";
import VersionDetailsDialog from "./components/VersionDetailsDialog";
import VersionRangeDialog from "./components/VersionRangeDialog";
import { parseChangelog, versionLine } from "./components/changelog";
import { fetchDetailedLines, versionRangeHash } from "./components/versionDetails";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    pageContainer: {
      padding: "20px",
      paddingTop: 85,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden"
    },
    pageHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: theme.spacing(2)
    },
    pageTitle: {
      fontSize: 22,
      fontWeight: 600
    },
    tabs: {
      minHeight: 40
    },
    tab: {
      minHeight: 40,
      textTransform: "none",
      fontSize: 14,
      "& .MuiTab-wrapper": {
        flexDirection: "row",
        gap: 6
      }
    },
    contentArea: {
      display: "flex",
      flex: 1,
      overflow: "hidden",
      borderRadius: 8,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper
    },
    sidebar: {
      width: 280,
      minWidth: 280,
      borderRight: `1px solid ${theme.palette.divider}`,
      overflowY: "auto",
      padding: theme.spacing(2)
    },
    content: {
      flex: 1,
      overflowY: "auto",
      padding: theme.spacing(4)
    },
    loading: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: 400,
      width: "100%",
      flexDirection: "column",
      gap: theme.spacing(2)
    },
    errorMessage: {
      textAlign: "center",
      color: theme.palette.text.secondary,
      padding: theme.spacing(4)
    },
    fallbackActions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(2)
    }
  })
);

/** Title of the details dialog: one version, or an interval of versions */
const detailsTitle = (from: string, to: string) => (from === to ? `Novidades em detalhe · Versão ${from}` : `Resumo de versões · ${from} → ${to}`);

const DocsPage: React.FC = () => {
  const classes = useStyles();
  const location = useLocation();
  // the new-version banner links here with ?novidades=<version>
  const requestedVersion = new URLSearchParams(location.search).get("novidades") || undefined;
  const [tab, setTab] = useState(requestedVersion ? 1 : 0);
  const { user } = useAuth();
  const { markSeen } = useSeenVersion(user?.id_user);
  const [manualContent, setManualContent] = useState<string>("");
  const [changelogContent, setChangelogContent] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [manualScrollEl, setManualScrollEl] = useState<HTMLDivElement | null>(null);
  const manualScrollRef = useCallback((node: HTMLDivElement | null) => {
    setManualScrollEl(node);
  }, []);
  const [detailedLines, setDetailedLines] = useState<string[] | null>(null);
  const mounted = useRef(true);

  useEffect(() => () => {
    mounted.current = false;
  }, []);

  // opening Novidades counts as seeing the new version: the banner and the "Novo" badge go away
  useEffect(() => {
    if (tab === 1) markSeen();
  }, [tab, markSeen]);

  // "Novidades em detalhe" reads the details page published with this build, for every user
  useEffect(() => {
    fetchDetailedLines().then((lines) => {
      if (mounted.current) setDetailedLines(lines);
    });
  }, []);

  useEffect(() => {
    const fetchDocs = async () => {
      setLoading(true);
      setError("");

      try {
        const [manualRes, changelogRes] = await Promise.all([
          fetch("/docs/MANUAL.md"),
          fetch("/docs/CHANGELOG.md")
        ]);

        if (manualRes.ok) {
          setManualContent(await manualRes.text());
        }

        if (changelogRes.ok) {
          setChangelogContent(await changelogRes.text());
        }

        if (!manualRes.ok && !changelogRes.ok) {
          setError(
            "Não foi possível carregar a documentação."
          );
        }
      } catch (err) {
        setError("Erro ao carregar documentação.");
      } finally {
        setLoading(false);
      }
    };

    fetchDocs();
  }, []);

  if (loading) {
    return (
      <>
        <Header title="Documentação" />
        <div className={classes.pageContainer}>
          <div className={classes.loading}>
            <CircularProgress />
            <Typography variant="body2" color="textSecondary">
              Carregando documentação...
            </Typography>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header title="Documentação" />
        <div className={classes.pageContainer}>
          <Typography className={classes.errorMessage} variant="body1">
            {error}
          </Typography>
        </div>
      </>
    );
  }

  return (
    <>
      <Header title="Documentação" />
      <div className={classes.pageContainer}>
        <div className={classes.pageHeader}>
          <Typography className={classes.pageTitle}>Documentação</Typography>
          <Tabs
            value={tab}
            onChange={(_, newValue) => setTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            className={classes.tabs}
          >
            <Tab
              icon={<MenuBookIcon fontSize="small" />}
              label="Manual"
              className={classes.tab}
            />
            <Tab
              icon={<NewReleasesIcon fontSize="small" />}
              label="Novidades"
              className={classes.tab}
            />
          </Tabs>
        </div>

        {tab === 0 && (
          <div className={classes.contentArea}>
            <div className={classes.sidebar}>
              <TableOfContents content={manualContent} scrollContainer={manualScrollEl} />
            </div>
            <div className={classes.content} ref={manualScrollRef}>
              <MarkdownRenderer content={manualContent} />
            </div>
          </div>
        )}

        {tab === 1 && (
          <div className={classes.contentArea}>
            <div className={classes.sidebar}>
              <ChangelogSidebar content={changelogContent} initialVersion={requestedVersion} />
            </div>
            <div className={classes.content}>
              <ChangelogContent
                content={changelogContent}
                detailedLines={detailedLines}
                initialVersion={requestedVersion}
                openDetails={!!requestedVersion}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

interface ChangelogContentProps {
  content: string;
  /** Version lines the details page documents; null hides "Novidades em detalhe" and "Resumo de versões" */
  detailedLines: string[] | null;
  /** Version to start on; the newest by default */
  initialVersion?: string;
  /** Opens Novidades em detalhe of the start version right away, when that version line has details */
  openDetails?: boolean;
}

const ChangelogContent: React.FC<ChangelogContentProps> = ({ content, detailedLines, initialVersion, openDetails }) => {
  const classes = useStyles();
  const versions = parseChangelog(content);
  const [selectedVersion, setSelectedVersion] = React.useState(
    (initialVersion && versions.some((v) => v.version === initialVersion) ? initialVersion : versions[0]?.version) || ""
  );
  const [details, setDetails] = useState<{ title: string; hash: string } | null>(null);
  const [rangeOpen, setRangeOpen] = useState(false);
  const detailsOpened = useRef(false);

  // the banner's "Ver novidades" lands straight on the version in detail, once
  useEffect(() => {
    if (!openDetails || detailsOpened.current || !detailedLines) return;

    detailsOpened.current = true;

    if (detailedLines.includes(versionLine(selectedVersion))) {
      setDetails({ title: detailsTitle(selectedVersion, selectedVersion), hash: versionRangeHash(selectedVersion, selectedVersion) });
    }
  }, [openDetails, detailedLines, selectedVersion]);

  React.useEffect(() => {
    const handleVersionChange = (e: CustomEvent) => {
      setSelectedVersion(e.detail);
    };

    window.addEventListener("changelog-version-select" as any, handleVersionChange);

    return () => {
      window.removeEventListener("changelog-version-select" as any, handleVersionChange);
    };
  }, []);

  if (versions.length === 0) {
    return <Typography color="textSecondary">Nenhuma versão encontrada.</Typography>;
  }

  const current = versions.find((v) => v.version === selectedVersion) || versions[0];
  const line = versionLine(current.version);
  const documented = !!detailedLines?.includes(line);
  const openRange = (from: string, to: string) => {
    setRangeOpen(false);
    setDetails({ title: detailsTitle(from, to), hash: versionRangeHash(from, to) });
  };

  const actions = detailedLines ? (
    <>
      <Button variant="outlined" color="primary" size="small" startIcon={<DateRangeIcon />} onClick={() => setRangeOpen(true)}>
        Resumo de versões
      </Button>
      <Tooltip title={documented ? "" : `A versão ${line} ainda não tem detalhes`}>
        <span>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<NewReleasesIcon />}
            disabled={!documented}
            onClick={() => openRange(current.version, current.version)}
          >
            Novidades em detalhe
          </Button>
        </span>
      </Tooltip>
    </>
  ) : undefined;

  const heading = current.date ? `v${current.version} — ${current.date}` : `v${current.version}`;
  const hasItems = current.sections.some((section) => section.items.length > 0);

  return (
    <>
      {hasItems ? (
        <ChangelogVersionView entry={current} actions={actions} />
      ) : (
        <>
          {actions && <div className={classes.fallbackActions}>{actions}</div>}
          <MarkdownRenderer content={`# ${heading}\n\n${current.body}`} />
        </>
      )}
      {detailedLines && (
        <>
          <VersionDetailsDialog open={!!details} title={details?.title || ""} hash={details?.hash || ""} onClose={() => setDetails(null)} />
          <VersionRangeDialog
            open={rangeOpen}
            versions={versions.map((v) => v.version)}
            initial={current.version}
            onClose={() => setRangeOpen(false)}
            onConfirm={openRange}
          />
        </>
      )}
    </>
  );
};

export default DocsPage;
